'use client';

import { useEffect } from 'react';

interface UseKeyboardNavigationParams {
  activeStep: number;
  activePhoto: number;
  hasNextSlideOrStep: boolean;
  prevStep: () => void;
  nextStep: () => void;
  prevSlide: () => void;
  nextSlide: () => void;
}

/**
 * Custom hook for handling keyboard navigation shortcuts
 * Supports arrow keys for navigation while avoiding interference with form inputs
 */
export function useKeyboardNavigation({
  activeStep,
  activePhoto,
  hasNextSlideOrStep,
  prevStep,
  nextStep,
  prevSlide,
  nextSlide,
}: UseKeyboardNavigationParams) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if focus is in an input or textarea
      const activeElement = document.activeElement;
      const isInputActive = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement;

      // Check if any modifier key is also pressed
      const isModifierPressed = event.altKey || event.ctrlKey || event.shiftKey || event.metaKey;

      // Only handle shortcuts if not in input/textarea and no modifier keys
      if (!isInputActive && !isModifierPressed) {
        switch (event.key) {
          case 'ArrowLeft':
            prevStep();
            break;
          case 'ArrowRight':
            nextStep();
            break;
          case 'ArrowUp':
            prevSlide();
            break;
          case 'ArrowDown':
            if (hasNextSlideOrStep) {
              nextSlide();
            }
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeStep, activePhoto, hasNextSlideOrStep, prevStep, nextStep, prevSlide, nextSlide]);
}
